import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import { StatCard } from "@/components/DashboardShell";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { IndianRupee, CreditCard, AlertTriangle, EyeOff, RefreshCw, Bell, Play, FileText, CheckCircle2, XCircle, Receipt } from "lucide-react";

const SUB_BADGE = {
  active: "bg-brand-soft text-brand", grace: "bg-amber-soft text-amber-warm",
  payment_failed: "bg-red-50 text-red-500", expired: "bg-red-50 text-red-500",
  suspended: "bg-slate-100 text-slate-500", cancelled: "bg-slate-100 text-slate-500",
};
const VIS_BADGE = { visible: "bg-brand-soft text-brand", visible_warning: "bg-amber-soft text-amber-warm", hidden: "bg-red-50 text-red-500" };
const VIS_LABEL = { visible: "Visible", visible_warning: "Visible (warning)", hidden: "Hidden" };

export function AdminSubscriptions() {
  const qc = useQueryClient();
  const { data: dash } = useQuery({ queryKey: ["sub-dash"], queryFn: () => api.get("/admin/subscription/dashboard").then((r) => r.data), refetchInterval: 10000 });
  const { data: list } = useQuery({ queryKey: ["sub-list"], queryFn: () => api.get("/admin/subscription/list").then((r) => r.data) });
  const [detail, setDetail] = useState(null);
  const [running, setRunning] = useState(false);

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["sub-dash"] }); qc.invalidateQueries({ queryKey: ["sub-list"] }); qc.invalidateQueries({ queryKey: ["a-dash"] }); };
  const charge = async (id, outcome) => { try { await api.post(`/admin/retailers/${id}/subscription/charge`, { outcome }); invalidate(); toast.success(outcome === "success" ? "Payment successful — store visible" : "Payment failed — grace period started"); } catch (e) { toast.error(apiError(e)); } };
  const remind = async (id) => { await api.post(`/admin/retailers/${id}/subscription/reminder`); toast.success("Reminder sent to retailer"); };
  const runBilling = async () => { setRunning(true); try { const { data } = await api.post("/admin/subscription/run-billing"); invalidate(); toast.success(`Billing run: ${data.renewed} renewed, ${data.hidden} hidden`); } catch (e) { toast.error(apiError(e)); } setRunning(false); };

  if (!dash || !list) return <Spinner />;

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Active Subscriptions" value={dash.active} icon={CheckCircle2} color="#059669" />
        <StatCard label="Monthly Recurring Rev." value={rupee(dash.mrr)} icon={IndianRupee} color="#8B5CF6" />
        <StatCard label="In Grace Period" value={dash.grace} icon={AlertTriangle} color="#F59E0B" />
        <StatCard label="Stores Hidden" value={dash.hidden} icon={EyeOff} color="#EF4444" />
        <StatCard label="Due This Week" value={dash.due_week} icon={CreditCard} color="#3B82F6" />
        <StatCard label="Payments Collected" value={rupee(dash.revenue_collected)} icon={IndianRupee} color="#059669" />
        <StatCard label="GST Collected" value={rupee(dash.gst_collected)} icon={Receipt} color="#0EA5E9" />
        <StatCard label="Failed Amount" value={rupee(dash.failed_amount)} icon={XCircle} color="#EF4444" />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-slate-500">Automated monthly billing · ₹599 + GST · store hidden after grace period, auto-restored on payment.</p>
        <button onClick={runBilling} disabled={running} data-testid="run-billing-btn" className="inline-flex items-center gap-1.5 rounded-xl bg-[#8B5CF6] px-4 py-2 text-sm font-bold text-white disabled:opacity-60">
          {running ? <Spinner className="h-4 w-4 border-white" /> : <Play className="h-4 w-4" />} Run Billing Cycle
        </button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100">
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Store</th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Status</th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Visibility</th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Total/mo</th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Next Billing</th>
            <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Actions</th>
          </tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id} className="border-b border-slate-50" data-testid="sub-row">
                <td className="px-4 py-3 font-semibold text-slate-800">{s.name}</td>
                <td className="px-4 py-3"><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${SUB_BADGE[s.subscription.status] || "bg-slate-100"}`}>{s.subscription.status}</span></td>
                <td className="px-4 py-3"><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${VIS_BADGE[s.visibility]}`}>{VIS_LABEL[s.visibility]}</span></td>
                <td className="px-4 py-3 font-mono">{rupee(s.subscription.total)}</td>
                <td className="px-4 py-3 text-slate-500">{(s.subscription.next_billing_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setDetail(s.id)} data-testid="sub-view-btn" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">View</button>
                    <button onClick={() => charge(s.id, "success")} data-testid="sub-charge-btn" className="rounded-lg bg-brand px-2.5 py-1 text-xs font-semibold text-white">Charge</button>
                    <button onClick={() => charge(s.id, "fail")} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-500">Fail (demo)</button>
                    <button onClick={() => remind(s.id)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600"><Bell className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SubscriptionPanel storeId={detail} onClose={() => setDetail(null)} onChanged={invalidate} />
    </div>
  );
}

function SubscriptionPanel({ storeId, onClose, onChanged }) {
  const [invoice, setInvoice] = useState(null);
  const { data } = useQuery({ queryKey: ["ret-sub", storeId], queryFn: () => api.get(`/admin/retailers/${storeId}/subscription`).then((r) => r.data), enabled: !!storeId });
  const charge = async (outcome) => { try { await api.post(`/admin/retailers/${storeId}/subscription/charge`, { outcome }); onChanged(); toast.success(outcome === "success" ? "Payment successful" : "Payment failed"); } catch (e) { toast.error(apiError(e)); } };

  return (
    <Dialog open={!!storeId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sub-panel-dialog" className="max-h-[88vh] max-w-lg overflow-auto">
        {!data ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-head">Subscription</DialogTitle>
              <DialogDescription>Loading subscription details…</DialogDescription>
            </DialogHeader>
            <div className="flex justify-center py-8"><Spinner /></div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head">{data.store.name} — Subscription</DialogTitle>
              <DialogDescription>Monthly Retailer Plan · recurring billing</DialogDescription>
            </DialogHeader>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between">
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${SUB_BADGE[data.subscription.status] || "bg-slate-100"}`}>{data.subscription.status}</span>
                <span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${VIS_BADGE[data.visibility]}`}>Store {VIS_LABEL[data.visibility]}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <Info label="Base Fee" value={rupee(data.subscription.base)} />
                <Info label={`GST (${data.subscription.gst_percent}%)`} value={rupee(data.subscription.gst)} />
                <Info label="Total Payable" value={rupee(data.subscription.total)} strong />
                <Info label="Next Billing" value={(data.subscription.next_billing_date || "").slice(0, 10)} />
                <Info label="Last Payment" value={(data.subscription.last_payment || "—").slice(0, 10)} />
                <Info label="Retry Count" value={data.subscription.retry_count} />
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => charge("success")} className="flex-1 rounded-xl bg-brand py-2 text-sm font-bold text-white">Charge / Renew</button>
                <button onClick={() => charge("fail")} className="rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-500">Simulate Fail</button>
              </div>
            </div>

            <h4 className="mt-2 font-head text-sm font-bold text-slate-800">Payment History</h4>
            <div className="space-y-2">
              {data.payments.length ? data.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-100 p-2.5">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{p.invoice_no}</p>
                    <p className="text-xs text-slate-400">{p.billing_period} · {rupee(p.total)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${p.status === "paid" ? "bg-brand-soft text-brand" : "bg-red-50 text-red-500"}`}>{p.status}</span>
                    {p.status === "paid" && <button onClick={() => setInvoice(p)} className="text-slate-400 hover:text-brand"><FileText className="h-4 w-4" /></button>}
                  </div>
                </div>
              )) : <p className="text-sm text-slate-400">No payments yet.</p>}
            </div>
            <InvoiceDialog invoice={invoice} store={data.store} onClose={() => setInvoice(null)} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value, strong }) => (
  <div><p className="text-xs text-slate-400">{label}</p><p className={`font-mono ${strong ? "text-base font-bold text-brand" : "text-slate-800"}`}>{value}</p></div>
);

export function InvoiceDialog({ invoice, store, onClose }) {
  if (!invoice) return null;
  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invoice-dialog" className="max-w-md">
        <DialogHeader><DialogTitle className="font-head">Tax Invoice</DialogTitle><DialogDescription>Grofkart platform subscription invoice</DialogDescription></DialogHeader>
        <div className="rounded-2xl border border-slate-200 p-5 text-sm">
          <div className="mb-3 flex items-center justify-between">
            <div><p className="font-head text-lg font-extrabold text-brand-deep">Grofkart</p><p className="text-xs text-slate-400">Platform Subscription</p></div>
            <div className="text-right"><p className="font-mono font-bold">{invoice.invoice_no}</p><p className="text-xs text-slate-400">{(invoice.created_at || "").slice(0, 10)}</p></div>
          </div>
          <p className="text-xs text-slate-500">Billed to: <b className="text-slate-700">{store?.name || invoice.store_name}</b></p>
          <p className="text-xs text-slate-500">Period: {invoice.billing_period}</p>
          <div className="mt-3 space-y-1 border-t border-dashed border-slate-200 pt-3">
            <Row label="Monthly Retailer Plan" value={rupee(invoice.base)} />
            <Row label={`GST (${invoice.gst_percent}%)`} value={rupee(invoice.gst)} />
            <div className="flex justify-between border-t border-slate-100 pt-1 font-head font-bold"><span>Total Paid</span><span className="font-mono text-brand">{rupee(invoice.total)}</span></div>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Txn: {invoice.txn_id} · {invoice.method}</p>
          <button onClick={() => window.print()} className="mt-3 w-full rounded-xl bg-slate-900 py-2 text-sm font-bold text-white">Download / Print</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
const Row = ({ label, value }) => (<div className="flex justify-between text-slate-600"><span>{label}</span><span className="font-mono">{value}</span></div>);

export function AdminSubPayments() {
  const [invoice, setInvoice] = useState(null);
  const { data } = useQuery({ queryKey: ["sub-payments"], queryFn: () => api.get("/admin/subscription/payments").then((r) => r.data) });
  if (!data) return <Spinner />;
  if (!data.length) return <EmptyState icon={Receipt} title="No subscription payments yet" subtitle="Charge a retailer or run the billing cycle." />;
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-slate-100">
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Invoice</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Store</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Total</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Status</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">Date</th>
          <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400"></th>
        </tr></thead>
        <tbody>
          {data.map((p) => (
            <tr key={p.id} className="border-b border-slate-50" data-testid="payment-row">
              <td className="px-4 py-3 font-mono font-semibold text-slate-800">{p.invoice_no}</td>
              <td className="px-4 py-3 text-slate-600">{p.store_name}</td>
              <td className="px-4 py-3 font-mono">{rupee(p.total)}</td>
              <td className="px-4 py-3"><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${p.status === "paid" ? "bg-brand-soft text-brand" : "bg-red-50 text-red-500"}`}>{p.status}</span></td>
              <td className="px-4 py-3 text-xs text-slate-400">{(p.created_at || "").slice(0, 10)}</td>
              <td className="px-4 py-3">{p.status === "paid" && <button onClick={() => setInvoice(p)} className="text-slate-400 hover:text-brand"><FileText className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <InvoiceDialog invoice={invoice} store={null} onClose={() => setInvoice(null)} />
    </div>
  );
}
